import React, { createContext, useContext, useState, useEffect } from 'react';
import { getPeriods, subscribeToPeriods, getOrganizationData, getPeriodImages } from './dataService';

const PeriodContext = createContext();

export const usePeriod = () => {
  const context = useContext(PeriodContext);
  if (!context) {
    throw new Error('usePeriod must be used within a PeriodProvider');
  }
  return context;
};

export const PeriodProvider = ({ children }) => {
  const [selectedPeriod, setSelectedPeriod] = useState('2026-2027');
  const [periods, setPeriods] = useState([]);
  const [organizationData, setOrganizationData] = useState(null);
  const [periodImages, setPeriodImages] = useState({ backgroundImage: null, homeImage: null });
  const [loading, setLoading] = useState(true);

  // Fetch periods and organization data on mount
  useEffect(() => {
    const fetchData = async () => {
      try {
        // Get organization data
        const orgData = await getOrganizationData();
        setOrganizationData(orgData);
        
        // Get periods
        const periodList = await getPeriods();
        setPeriods(periodList);
        
        // Set active period or default
        const activePeriod = periodList.find(p => p.isActive);
        if (activePeriod) {
          setSelectedPeriod(activePeriod.tahun);
        } else if (periodList.length > 0) {
          setSelectedPeriod(periodList[0].tahun);
        }
      } catch (error) {
        console.error("Error fetching data:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();

    // Subscribe to real-time updates
    const unsubscribePeriods = subscribeToPeriods((updatedPeriods) => {
      setPeriods(updatedPeriods);
    });

    return () => {
      unsubscribePeriods();
    };
  }, []);

  // Fetch period images when selectedPeriod changes
  useEffect(() => {
    const fetchPeriodImages = async () => {
      try {
        const images = await getPeriodImages(selectedPeriod);
        setPeriodImages(images);
      } catch (error) {
        console.error("Error fetching period images:", error);
        setPeriodImages({ backgroundImage: null, homeImage: null });
      }
    };

    if (selectedPeriod) {
      fetchPeriodImages();
    }
  }, [selectedPeriod]);

  const refreshOrganizationData = async () => {
    const orgData = await getOrganizationData();
    setOrganizationData(orgData);
  };

  const refreshPeriods = async () => {
    const periodList = await getPeriods();
    setPeriods(periodList);
  };

  const refreshPeriodImages = async () => {
    if (selectedPeriod) {
      const images = await getPeriodImages(selectedPeriod);
      setPeriodImages(images);
    }
  };

  return (
    <PeriodContext.Provider value={{ 
      selectedPeriod, 
      setSelectedPeriod,
      periods,
      organizationData,
      periodImages,
      loading,
      refreshOrganizationData,
      refreshPeriods,
      refreshPeriodImages
    }}>
      {children}
    </PeriodContext.Provider>
  );
};

